// Live progress modal for a Plato sync.
// Shows each stage as it happens (fetch → reconcile → import → done), a progress
// bar while importing, and — importantly — a clear "rate limited, retrying" state
// so staff know a slow sync is still working, not stuck.

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2, Circle, AlertTriangle, Clock } from 'lucide-react';
import type { PlatoSyncProgress, PlatoSyncStage } from '@/types/plato';

interface PlatoSyncProgressModalProps {
  isOpen: boolean;
  progress: PlatoSyncProgress | null;
  onClose: () => void;
}

// Ordered stages shown in the stepper. 'retrying' maps onto 'fetching'.
const STAGE_ORDER: { key: PlatoSyncStage; label: string }[] = [
  { key: 'fetching', label: 'Fetch appointments from Plato' },
  { key: 'loading', label: 'Load schedule, doctors & staff' },
  { key: 'reconciling', label: 'Remove stale / duplicate rows' },
  { key: 'importing', label: 'Import appointments & assign nurses' },
  { key: 'done', label: 'Done' },
];

const normalizeStage = (stage: PlatoSyncStage): PlatoSyncStage =>
  stage === 'retrying' ? 'fetching' : stage;

const stageIndex = (stage: PlatoSyncStage): number =>
  STAGE_ORDER.findIndex(s => s.key === normalizeStage(stage));

const PlatoSyncProgressModal = ({ isOpen, progress, onClose }: PlatoSyncProgressModalProps) => {
  const stage = progress?.stage ?? 'idle';
  const isError = stage === 'error';
  const isDone = stage === 'done';
  const isRetrying = stage === 'retrying';
  const canClose = isDone || isError;
  const activeIdx = stageIndex(stage);

  const pct =
    progress?.total && progress.total > 0
      ? Math.round(((progress.current ?? 0) / progress.total) * 100)
      : null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Only allow closing when finished or errored — never mid-sync.
        if (!open && canClose) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isError ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : isDone ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isError ? 'Sync failed' : isDone ? 'Sync complete' : 'Syncing with Plato…'}
          </DialogTitle>
        </DialogHeader>

        {/* Current message */}
        <p className={`text-sm ${isError ? 'text-destructive' : 'text-muted-foreground'}`}>
          {progress?.message ?? 'Preparing…'}
        </p>

        {/* Rate-limit / retry banner */}
        {isRetrying && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Respecting Plato's rate limit — this can take a little while.
              {typeof progress?.attempt === 'number' && progress?.maxAttempts
                ? ` Retry ${progress.attempt} of ${progress.maxAttempts}.`
                : ''}{' '}
              Please keep this window open.
            </span>
          </div>
        )}

        {/* Progress bar while importing */}
        {pct !== null && !isError && (
          <div className="space-y-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-right text-xs text-muted-foreground">
              {progress?.current ?? 0} / {progress?.total} ({pct}%)
            </div>
          </div>
        )}

        {/* Stage checklist */}
        <ol className="mt-1 space-y-2">
          {STAGE_ORDER.map((s, idx) => {
            const complete = isDone || (activeIdx >= 0 && idx < activeIdx);
            const active = !isDone && !isError && idx === activeIdx;
            return (
              <li key={s.key} className="flex items-center gap-2 text-sm">
                {complete ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : active ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/40" />
                )}
                <span
                  className={
                    complete
                      ? 'text-foreground'
                      : active
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground'
                  }
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
      </DialogContent>
    </Dialog>
  );
};

export default PlatoSyncProgressModal;
